import LegalLayout from '@/pages/legal/components/LegalLayout';

export default function CodeOfConduct() {
  return (
    <LegalLayout title="Code of Conduct" lastUpdated="May 2026" current="/code-of-conduct">
      <p>
        Sapphire Media Group is built by young people, and a lot of those young people are
        new to working in public — pushing first commits, hosting first shows, filing
        first stories. This Code of Conduct sets the bar for how we treat each other so
        that staying remains worth it.
      </p>
      <p>
        It applies to every space we run: this website, our Discord server, our public
        repositories, our radio station and chats, and any in-person event we organise or
        attend on behalf of Sapphire.
      </p>

      <h2>The short version</h2>
      <ul>
        <li>Be kind. Assume good faith.</li>
        <li>Make space for people who know less than you do today.</li>
        <li>Disagree on the work, not the person.</li>
        <li>Don't be a creep.</li>
      </ul>

      <h2>Expected behaviour</h2>
      <ul>
        <li>Use welcoming and inclusive language.</li>
        <li>Be respectful of differing viewpoints, identities, and experiences.</li>
        <li>Accept constructive feedback gracefully, and give it generously.</li>
        <li>
          Centre the impact of your actions over your intent. If you've upset someone,
          listen first.
        </li>
        <li>
          Help newcomers. If you've shipped a few PRs or hosted a few shows, you're a
          mentor whether you signed up to be or not.
        </li>
      </ul>

      <h2>Unacceptable behaviour</h2>
      <ul>
        <li>
          Harassment of any kind: slurs, sustained insults, sexualised language or imagery
          aimed at someone, threats, doxxing, or stalking.
        </li>
        <li>
          Discrimination based on race, ethnicity, national origin, religion, gender
          identity, sexual orientation, disability, neurotype, age, or appearance.
        </li>
        <li>
          Sexual or romantic advances toward minors, or any sexualised contact between
          adults and minors. This is non-negotiable and results in an immediate ban.
        </li>
        <li>Sharing private communications or personal information without consent.</li>
        <li>
          Sustained disruption of community spaces, on-air programming, or events.
        </li>
        <li>Encouraging any of the above.</li>
      </ul>

      <h2>Working with minors</h2>
      <p>
        Many Sapphire contributors are under 18. Adults working with the project — mentors,
        editors, sponsors — are expected to:
      </p>
      <ul>
        <li>Keep mentorship interactions in public or recorded channels by default.</li>
        <li>
          Decline DMs from minors that move outside the project's scope; redirect to a
          public channel.
        </li>
        <li>
          Never request or share photos, contact details, or location information from
          minors outside what is needed for the work.
        </li>
        <li>
          Report any concerning interaction (yours or someone else's) to the contacts
          below immediately.
        </li>
      </ul>

      <h2>Reporting</h2>
      <p>
        If you experience or witness behaviour that violates this Code, please tell us.
        You can:
      </p>
      <ul>
        <li>
          Email{' '}
          <a href="mailto:conduct@sapphiremedia.group">conduct@sapphiremedia.group</a>{' '}
          — a small, named group of moderators reads this.
        </li>
        <li>
          Email{' '}
          <a href="mailto:hello@sapphiremedia.group">hello@sapphiremedia.group</a> if the
          conduct address is part of the problem.
        </li>
        <li>
          For urgent or serious safeguarding concerns involving a minor, contact Sapphire Media Group’s director directly at{' '}
          <a href="mailto:conduct@sapphiremedia.group">conduct@sapphiremedia.group</a>.
        </li>
      </ul>
      <p>
        Reports are kept as private as we can keep them while still acting on them. We
        will not retaliate against anyone who reports in good faith, and we will not name
        you publicly without your consent.
      </p>

      <h2>Enforcement</h2>
      <p>
        Moderators may, depending on severity and pattern, take any of the following
        actions: a private word, a public correction, a temporary mute or ban, removal
        from a project or show, or a permanent ban from all Sapphire spaces. We aim to be
        proportionate, but we will err on the side of community safety, especially where
        minors are involved.
      </p>
      <p>
        Decisions can be appealed in writing to{' '}
        <a href="mailto:hello@sapphiremedia.group">hello@sapphiremedia.group</a>.
      </p>

      <h2>Attribution</h2>
      <p>
        Adapted in part from the{' '}
        <a href="https://www.contributor-covenant.org/" target="_blank" rel="noopener noreferrer">
          Contributor Covenant
        </a>{' '}
        v2.1, with additional safeguarding language specific to a youth-led organisation.
      </p>
    </LegalLayout>
  );
}
